/**
 * Recipe Engine Service — isolated calculation logic
 * Handles ingredient costing, margin analysis, and price change detection.
 */

import type { Ingredient, RecipeIngredient, Recipe, Allergen } from '../types';

// ─── Core Calculations ──────────────────────────────────────────────────────

/** Calculate line cost for a single ingredient usage */
export function calcLineCost(usageQty: number, unitCost: number): number {
  return Math.round(usageQty * unitCost * 1000) / 1000;
}

/** Recalculate all line costs and total cost for a recipe */
export function recalcRecipe(
  recipeIngredients: RecipeIngredient[],
  pantry: Map<string, Ingredient>,
): { ingredients: RecipeIngredient[]; totalCost: number } {
  const updated = recipeIngredients.map(ri => {
    const pantryItem = pantry.get(ri.ingredientId);
    const unitCost = pantryItem?.purchasePrice ?? ri.unitCost;
    const lineCost = calcLineCost(ri.usageQty, unitCost);
    return { ...ri, unitCost, lineCost, ingredientName: pantryItem?.name ?? ri.ingredientName };
  });
  const totalCost = Math.round(updated.reduce((sum, ri) => sum + ri.lineCost, 0) * 100) / 100;
  return { ingredients: updated, totalCost };
}

/** Calculate food cost percentage: (totalCost / sellingPrice) * 100 */
export function foodCostPct(totalCost: number, sellingPrice: number): number {
  if (sellingPrice <= 0) return 0;
  return Math.round((totalCost / sellingPrice) * 10000) / 100;
}

/** Calculate profit margin: sellingPrice - totalCost */
export function profitMargin(totalCost: number, sellingPrice: number): number {
  return Math.round((sellingPrice - totalCost) * 100) / 100;
}

// ─── Price Change Detection ────────────────────────────────────────────────

export interface PriceAlert {
  ingredientId: string;
  ingredientName: string;
  oldPrice: number;
  newPrice: number;
  changePct: number;
  affectedItems: { itemId: string; itemName: string; profileId: string }[];
}

/** Detect which menu items are affected by ingredient price changes */
export function detectPriceAlerts(
  priceChanges: { ingredientId: string; ingredientName: string; oldPrice: number; newPrice: number }[],
  recipes: Recipe[],
  itemNameMap: Map<string, string>,
): PriceAlert[] {
  return priceChanges.map(pc => {
    const changePct = pc.oldPrice > 0
      ? Math.round(((pc.newPrice - pc.oldPrice) / pc.oldPrice) * 10000) / 100
      : 0;
    const affectedItems = recipes
      .filter(r => r.ingredients.some(ri => ri.ingredientId === pc.ingredientId))
      .map(r => ({
        itemId: r.menuItemId,
        itemName: itemNameMap.get(r.menuItemId) || 'Unknown',
        profileId: r.profileId,
      }));
    return { ...pc, changePct, affectedItems };
  }).filter(a => a.affectedItems.length > 0);
}

// ─── Recipe Builder Helpers ────────────────────────────────────────────────

/** Create a new RecipeIngredient from a pantry item */
export function createRecipeIngredient(
  ingredient: Ingredient,
  usageQty: number = 0,
): RecipeIngredient {
  return {
    ingredientId: ingredient.id,
    ingredientName: ingredient.name,
    usageQty,
    usageUnit: ingredient.purchaseUnit,
    unitCost: ingredient.purchasePrice,
    lineCost: calcLineCost(usageQty, ingredient.purchasePrice),
  };
}

/** Create a blank recipe for a menu item */
export function createBlankRecipe(
  id: string,
  menuItemId: string,
  profileId: string,
  orgId: string,
): Recipe {
  return {
    id,
    menuItemId,
    profileId,
    orgId,
    ingredients: [],
    totalCost: 0,
    prepSteps: '',
    standardYield: 1,
    allergens: [],
  };
}

// ─── Export Helpers ─────────────────────────────────────────────────────────

/** Format recipe for Kitchen Card (NO prices — kitchen staff don't see money) */
export function formatKitchenCard(recipe: Recipe, itemName: string, itemImage?: string): {
  itemName: string;
  itemImage?: string;
  ingredients: { name: string; qty: string }[];
  steps: string[];
  allergens: Allergen[];
  yield: number;
  prepTime?: number;
} {
  return {
    itemName,
    itemImage,
    ingredients: recipe.ingredients.map(ri => ({
      name: ri.ingredientName,
      qty: `${ri.usageQty} ${ri.usageUnit}`,
    })),
    steps: recipe.prepSteps
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean),
    allergens: recipe.allergens,
    yield: recipe.standardYield,
    prepTime: recipe.prepTime,
  };
}
